/**
 * Export Utilities
 * Provides functions for exporting data in various formats (JSON, CSV, XML)
 */

/**
 * Download a file with the given content and filename
 */
export function downloadFile(content: string, filename: string, mimeType: string = 'text/plain') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Convert JSON to CSV format
 */
export function jsonToCSV(data: any): string {
  if (!data) return '';

  // Handle array of objects
  let items: any[] = [];
  
  if (Array.isArray(data)) {
    items = data;
  } else if (data.items && Array.isArray(data.items)) {
    items = data.items;
  } else if (data.data && Array.isArray(data.data)) {
    items = data.data;
  } else if (data.results && Array.isArray(data.results)) {
    items = data.results;
  } else if (data.rows && Array.isArray(data.rows)) {
    items = data.rows;
  } else {
    // Single object - treat as single row
    items = [data];
  }

  if (items.length === 0) {
    return '';
  }

  // Filter out non-object items
  const objectItems = items.filter(item => item && typeof item === 'object' && !Array.isArray(item));
  
  if (objectItems.length === 0) {
    return '';
  }

  // Get all unique keys
  const allKeys = new Set<string>();
  objectItems.forEach(item => {
    Object.keys(item).forEach(key => allKeys.add(key));
  });
  
  const keys = Array.from(allKeys);

  // Create CSV header
  const header = keys.map(key => `"${key}"`).join(',');

  // Create CSV rows
  const rows = objectItems.map(item => {
    return keys.map(key => {
      const value = item[key];
      
      if (value === null || value === undefined) {
        return '""';
      }
      
      if (typeof value === 'object') {
        return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
      }
      
      // Escape quotes and wrap in quotes
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    }).join(',');
  });

  return [header, ...rows].join('\n');
}

/**
 * Convert JSON to XML format
 */
export function jsonToXML(data: any, rootName: string = 'root'): string {
  const escapeXML = (str: string): string => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const convertValue = (value: any, key: string, indent: string = ''): string => {
    if (value === null || value === undefined) {
      return `${indent}<${key} />\n`;
    }

    if (Array.isArray(value)) {
      return value.map(item => convertValue(item, key, indent)).join('');
    }

    if (typeof value === 'object') {
      const children = Object.entries(value)
        .map(([k, v]) => convertValue(v, k, indent + '  '))
        .join('');
      return `${indent}<${key}>\n${children}${indent}</${key}>\n`;
    }

    // Primitive value
    return `${indent}<${key}>${escapeXML(String(value))}</${key}>\n`;
  };

  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  
  if (Array.isArray(data)) {
    xml += `<${rootName}>\n`;
    data.forEach((item, index) => {
      xml += convertValue(item, 'item', '  ');
    });
    xml += `</${rootName}>`;
  } else {
    xml += convertValue(data, rootName, '');
  }

  return xml;
}

/**
 * Format/beautify JSON with proper indentation
 */
export function formatJSON(json: string): string {
  try {
    const parsed = JSON.parse(json);
    return JSON.stringify(parsed, null, 2);
  } catch (error) {
    throw new Error('Invalid JSON: ' + (error as Error).message);
  }
}

/**
 * Validate JSON string
 */
export function validateJSON(json: string): { valid: boolean; error?: string } {
  try {
    JSON.parse(json);
    return { valid: true };
  } catch (error) {
    return { 
      valid: false, 
      error: (error as Error).message 
    };
  }
}

/**
 * Generate code snippet for various languages
 */
export function generateCodeSnippet(data: any, language: 'javascript' | 'python' | 'curl'): string {
  const jsonString = JSON.stringify(data, null, 2);

  switch (language) {
    case 'javascript':
      return `const data = ${jsonString};\n\n// Use the data\nconsole.log(data);`;
    
    case 'python':
      return `import json\n\ndata = json.loads('''${jsonString}''')\n\n# Use the data\nprint(data)`;
    
    case 'curl':
      // This is a placeholder - actual curl command would need request details
      return `# Curl command would be generated from request details\ncurl -X POST https://api.example.com/endpoint \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(data)}'`;
    
    default:
      return jsonString;
  }
}
